import { AxiosResponse } from "axios";
import httpApi from "@src/utils/httpApi";
import { ApiResponse } from "@src/types/httpApi";

export type ArticleStatus = "draft" | "submitted" | "published";

export interface CreateArticleBody {
  subtitle?: string;
  header?: string;
  createdDate?: string;
  curator?: string;
  playlist?: string | null;
  body: string;
  publishedDate?: string | null;
  featuredImage?: number | string;
  featuredImageCaption?: string;
  description?: string;
  urlSlug?: string;
  articleStatus?: ArticleStatus;
}

export const createArticleApi = async (
  articleData: CreateArticleBody
): Promise<AxiosResponse<ApiResponse<any>>> => {
  const payload: any = {
    data: {
      header: articleData.header || null,
      createdDate: articleData.createdDate || null,
      body: articleData.body,
      publishedDate: articleData.publishedDate || null,
      featuredImageCaption: articleData.featuredImageCaption || null,
      description: articleData.description || null,
      urlSlug: articleData.urlSlug || null,
      articleStatus: articleData.articleStatus || "draft",
    },
  };

  if (articleData.curator) {
    payload.data.curator = {
      connect: [{ documentId: articleData.curator }],
    };
  }

  if (articleData.playlist) {
    payload.data.playlist = {
      connect: [{ documentId: articleData.playlist }],
    };
  }

  if (articleData.featuredImage) {
    payload.data.featuredImage = articleData.featuredImage;
  }

  return httpApi.post("/api/articles", payload);
};

export const uploadArticleImage = async (
  file: File
): Promise<AxiosResponse<{ id: number; documentId: string }[]>> => {
  const formData = new FormData();
  formData.append("files", file);
  return httpApi.post("/api/upload", formData);
};

export interface GetArticlesParams {
  curatorId?: string;
  page?: number;
  pageSize?: number;
  searchText?: string;
}

export interface IArticle {
  id: number;
  documentId: string;
  header?: string;
  subtitle?: string;
  body: string;
  description?: string;
  urlSlug?: string;
  articleStatus: ArticleStatus;
  featuredImage?: any;
  featuredImageCaption?: string;
  curator?: any;
  playlist?: any;
  createdAt: string;
  updatedAt: string;
  publishedDate?: string;
  createdDate?: string;
}

export const getArticleApi = async (
  articleId: string
): Promise<AxiosResponse<ApiResponse<IArticle>>> => {
  return httpApi.get(`/api/articles/${articleId}`, {
    params: {
      populate: {
        featuredImage: { fields: ["*"] },
        curator: { fields: ["name", "documentId"] },
        playlist: { fields: ["title", "documentId"] },
      },
    },
  });
};

export const updateArticleApi = async (
  articleId: string,
  articleData: Partial<CreateArticleBody>
): Promise<AxiosResponse<ApiResponse<any>>> => {
  const payload: any = {
    data: {},
  };

  if (articleData.subtitle !== undefined) {
    payload.data.subtitle = articleData.subtitle || null;
  }
  if (articleData.header !== undefined) {
    payload.data.header = articleData.header || null;
  }
  if (articleData.body !== undefined) {
    payload.data.body = articleData.body;
  }
  if (articleData.createdDate !== undefined) {
    payload.data.createdDate = articleData.createdDate || null;
  }
  if (articleData.publishedDate !== undefined) {
    payload.data.publishedDate = articleData.publishedDate || null;
  }
  if (articleData.featuredImageCaption !== undefined) {
    payload.data.featuredImageCaption =
      articleData.featuredImageCaption || null;
  }
  if (articleData.description !== undefined) {
    payload.data.description = articleData.description || null;
  }
  if (articleData.urlSlug !== undefined) {
    payload.data.urlSlug = articleData.urlSlug || null;
  }
  if (articleData.articleStatus !== undefined) {
    payload.data.articleStatus = articleData.articleStatus;
  }

  if (articleData.curator) {
    payload.data.curator = {
      connect: [{ documentId: articleData.curator }],
    };
  }

  if (articleData.playlist === null) {
    payload.data.playlist = null;
  } else if (articleData.playlist) {
    payload.data.playlist = {
      connect: [{ documentId: articleData.playlist }],
    };
  }

  if (articleData.featuredImage !== undefined) {
    payload.data.featuredImage = articleData.featuredImage || null;
  }

  return httpApi.put(`/api/articles/${articleId}`, payload);
};

export const getArticles = async (
  params: GetArticlesParams
): Promise<AxiosResponse<ApiResponse<IArticle[]>>> => {
  const queryParams: any = {
    populate: {
      featuredImage: {
        fields: ["*"],
      },
      curator: {
        fields: ["name", "documentId"],
      },
      playlist: {
        fields: ["title", "documentId"],
      },
    },
    pagination: {
      page: params.page || 1,
      pageSize: params.pageSize || 25,
    },
    sort: ["updatedAt:desc"],
  };

  if (params.curatorId) {
    queryParams["filters[curator][documentId][$eq]"] = params.curatorId;
  }

  if (params.searchText) {
    queryParams["filters[$or][0][header][$containsi]"] = params.searchText;
    queryParams["filters[$or][1][description][$containsi]"] = params.searchText;
  }

  return httpApi.get("/api/articles", { params: queryParams });
};
